import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, KpiCard, Spinner } from '@kss/ui';
import { CalendarDays, RefreshCw, Search, Wallet } from 'lucide-react';
import { pickList, portalApi } from './portalApi';
import { usePortalSession } from './portalSession';

/**
 * Customer-facing payment history.
 *
 * Pulls /sales/SalesPayment for the signed-in customer and shows the
 * full ledger with KPIs (total paid, payments YTD, last 30 days, last
 * payment) plus a search box.
 */

interface PaymentRow {
  id?: number | string;
  salesPaymentId?: string;
  paymentNumber?: string;
  paymentDate?: string;
  amount?: number;
  paymentMode?: string;
  reference?: string;
  notes?: string;
  invoiceNumber?: string;
  invoiceId?: string;
}

export default function PortalPayments() {
  const session = usePortalSession();
  const [rows, setRows] = useState<PaymentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  async function load() {
    if (!session?.customerId) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await portalApi.get<unknown>('/sales/SalesPayment', {
        query: { customerId: session.customerId },
      });
      setRows(pickList<PaymentRow>(r));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [session?.customerId]);

  const kpis = useMemo(() => {
    const yearStart = new Date(new Date().getFullYear(), 0, 1).getTime();
    const monthAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    let total = 0;
    let ytd = 0;
    let last30 = 0;
    let lastTs = 0;
    for (const p of rows) {
      const amt = Number(p.amount ?? 0);
      const t = Date.parse(String(p.paymentDate ?? ''));
      total += amt;
      if (!Number.isNaN(t)) {
        if (t >= yearStart) ytd += amt;
        if (t >= monthAgo) last30 += amt;
        if (t > lastTs) lastTs = t;
      }
    }
    return { total, ytd, last30, last: lastTs ? new Date(lastTs) : null };
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((p) => {
        if (!q) return true;
        const hay = `${p.paymentNumber ?? ''} ${p.salesPaymentId ?? ''} ${p.reference ?? ''} ${p.invoiceNumber ?? ''} ${p.notes ?? ''} ${p.paymentMode ?? ''}`.toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => Date.parse(String(b.paymentDate ?? '')) - Date.parse(String(a.paymentDate ?? '')));
  }, [rows, search]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Payment history</h2>
          <p className="text-xs text-[var(--color-text-secondary)]">
            All payments you&rsquo;ve made to this supplier.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Reload
        </Button>
      </div>

      {err && <Alert variant="warning">{err}</Alert>}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="Total paid"
          value={`₹ ${inr(kpis.total)}`}
          subtitle="All time"
          icon={<Wallet className="h-5 w-5 text-success" />}
        />
        <KpiCard
          title="This year"
          value={`₹ ${inr(kpis.ytd)}`}
          subtitle="Since 1 Jan"
          icon={<CalendarDays className="h-5 w-5 text-info" />}
        />
        <KpiCard
          title="Last 30 days"
          value={`₹ ${inr(kpis.last30)}`}
          subtitle="Recent activity"
          icon={<CalendarDays className="h-5 w-5 text-primary" />}
        />
        <KpiCard
          title="Last payment"
          value={kpis.last ? kpis.last.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}
          subtitle={kpis.last ? relativeTime(kpis.last.toISOString()) : 'No history'}
          icon={<Wallet className="h-5 w-5 text-primary" />}
        />
      </div>

      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--color-text-muted)]" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by reference, mode or invoice…"
              className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] py-1.5 pl-7 pr-2 text-sm outline-none focus:border-[var(--color-primary)]"
            />
          </div>
        </div>

        {loading && filtered.length === 0 ? (
          <div className="flex items-center gap-2 p-6 text-sm text-[var(--color-text-muted)]">
            <Spinner /> Loading payments…
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-10 text-center text-sm text-[var(--color-text-muted)]">
            <Wallet className="h-8 w-8 opacity-40" />
            <div>No payments yet.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-[11px] uppercase tracking-wider text-[var(--color-text-muted)]">
                  <th className="py-2 pr-2">Date</th>
                  <th className="py-2 pr-2">Reference</th>
                  <th className="py-2 pr-2">Mode</th>
                  <th className="py-2 pr-2">Against invoice</th>
                  <th className="py-2 pr-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)]">
                {filtered.map((p) => {
                  const id = String(p.salesPaymentId ?? p.id ?? '');
                  return (
                    <tr key={id}>
                      <td className="py-2 pr-2 whitespace-nowrap">{fmtDate(p.paymentDate)}</td>
                      <td className="py-2 pr-2">
                        <div>{p.paymentNumber ?? id}</div>
                        {p.reference && (
                          <div className="text-[11px] text-[var(--color-text-muted)]">{p.reference}</div>
                        )}
                      </td>
                      <td className="py-2 pr-2">
                        <ModeBadge v={String(p.paymentMode ?? '')} />
                      </td>
                      <td className="py-2 pr-2 text-[var(--color-text-secondary)]">
                        {p.invoiceNumber ?? p.invoiceId ?? '—'}
                      </td>
                      <td className="py-2 pr-2 text-right font-semibold tabular-nums text-success">
                        ₹ {inr(Number(p.amount ?? 0))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function ModeBadge({ v }: { v: string }) {
  const s = v.toLowerCase();
  let cls = 'border-content-muted/40 bg-content-muted/10 text-content-muted';
  if (s.includes('cash')) cls = 'border-success/40 bg-success/10 text-success';
  else if (s.includes('upi')) cls = 'border-primary/40 bg-primary/10 text-primary';
  else if (s.includes('card')) cls = 'border-info/40 bg-info/10 text-info';
  else if (s.includes('bank') || s.includes('neft') || s.includes('rtgs')) cls = 'border-warning/40 bg-warning/10 text-warning';
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] capitalize ${cls}`}>
      {v || '—'}
    </span>
  );
}

function fmtDate(v: unknown): string {
  if (typeof v !== 'string' || !v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v.slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function relativeTime(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const diff = Math.round((Date.now() - t) / 1000);
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

function inr(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
